import { get, findIndex, isEmpty } from 'lodash';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { ICardScoped, IMeta } from 'ui/interfaces/entities/clinical';
import update from 'immutability-helper';
import actions from './action';
import { isUserAuthorised } from 'patientCommons/helpers/user';

export interface IReducerState {
  config: {
    loading: boolean;
    error: boolean;
    empty: boolean;
  };
  cards?: ICardScoped[];
  meta?: IMeta;
}

const INITIAL_STATE: IReducerState = {
  config: {
    loading: false,
    error: false,
    empty: false,
  },
  cards: [],
};

const validatePermissions = (requiredPermissions: string[]) =>
  !isEmpty(requiredPermissions)
    ? requiredPermissions.some((permission: string) => isUserAuthorised([permission]))
    : true;

const reducer = reducerWithInitialState<IReducerState>(INITIAL_STATE)
  .case(actions.getClinicalConfig.started, state =>
    update(state, {
      config: {
        loading: { $set: true },
      },
    }),
  )
  .case(actions.getClinicalConfig.done, (state, payload) => {
    const data: ICardScoped[] = get(payload, 'result.data', []);
    const meta: IMeta = get(payload, 'result.meta', []);
    const cards = data
      .filter(e => validatePermissions(get(e, 'config.required_permissions', [])) && get(e, 'config.visible', false))
      .sort((a, b) => {
        const v1 = get(a, 'config.priority', 0);
        const v2 = get(b, 'config.priority', 0);
        return v1 - v2;
      });
    return update(state, {
      config: {
        loading: { $set: false },
        empty: { $set: cards.length === 0 },
        error: { $set: false },
      },
      cards: { $set: cards },
      meta: { $set: meta },
    });
  })
  .case(actions.getClinicalConfig.failed, (state, payload) =>
    update(state, {
      config: {
        loading: { $set: false },
        error: {
          $set: get(payload, 'error.error.message', 'Something Went Wrong!'),
        },
      },
    }),
  )
  .case(actions.getWidgetData.started, (state, payload) => {
    const id = payload.id;
    const index = findIndex(state.cards, e => e.config.id === id);
    return update(state, {
      cards: {
        [index]: {
          loading: { $set: true },
        },
      },
    });
  })
  .case(actions.getWidgetData.done, (state, payload) => {
    const id = payload.params.id;
    const index = findIndex(state.cards, e => e.config.id === id);
    const data = get(payload, 'result.data', []);
    const isDataEmpty = Array.isArray(data) ? data.length === 0 : Object.keys(data).length === 0;
    return update(state, {
      cards: {
        [index]: {
          loading: { $set: false },
          error: { $set: false },
          data: { $set: data },
          schema: { $set: get(payload, 'result.schema', undefined) },
          empty: { $set: isDataEmpty },
        },
      },
    });
  })
  .case(actions.getWidgetData.failed, (state, payload) => {
    const id = payload.params.id;
    const index = findIndex(state.cards, e => e.config.id === id);
    return update(state, {
      cards: {
        [index]: {
          loading: { $set: false },
          error: {
            $set: get(payload, 'error.error.message', 'Something Went Wrong!'),
          },
        },
      },
    });
  })
  .build();

export default reducer;
